import { Component, OnInit, Inject, PLATFORM_ID, TransferState, makeStateKey } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { BlogService } from 'src/app/_services/blog.service';
import { User } from 'src/app/_models/user';
import { ActivatedRoute, Router } from '@angular/router';
import { BlogPost } from 'src/app/_models/blog-post';
import { environment } from 'src/environments/environment';
import { IPagination } from 'src/app/_models/pagination';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { Title, Meta } from '@angular/platform-browser';

@Component({
    selector: 'app-blog-post',
    templateUrl: './blog-post.component.html',
    styleUrls: ['./blog-post.component.css'],
    standalone: false
})
export class BlogPostComponent implements OnInit {

  user: User = null;
  title = 'blog';
  loading = true;
  loadingComments = true;
  blogPostId: string;
  blog: BlogPost;
  config: any;
  comments: Comment[] = [];
  form: UntypedFormGroup;
  currentUrl = '';
  submitting = false;

  // TransferState key
  private readonly BLOG_POST_KEY = makeStateKey<BlogPost>('blog_post');

constructor(
    private titleService: Title,
    private meta: Meta,
    private blogService: BlogService,
    private fb: UntypedFormBuilder,
    private activateRoute: ActivatedRoute,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private transferState: TransferState) {
    this.titleService.setTitle(this.title);
    if (isPlatformBrowser(this.platformId)) {
        this.user = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
    }
    this.blogPostId = this.activateRoute.snapshot.paramMap.get('id') ?? '';
    this.currentUrl = this.router.url;
  }

  ngOnInit() {
    this.loadBlogPost();
    this.loadComments();
    this.buildFormComment();
  }

  loadBlogPost() {
    if (this.transferState.hasKey(this.BLOG_POST_KEY)) {
      // Use cached data from server
      this.blog = this.transferState.get(this.BLOG_POST_KEY, null);
      this.transferState.remove(this.BLOG_POST_KEY);
      
      if (this.blog) {
        this.titleService.setTitle(this.blog.title.concat(' - ' + this.title));
        this.setMetaTags();
      }
      this.loading = false;
    } else {
      // Fetch from API
      this.blogService.loadBlogPost(parseInt(this.blogPostId, 10)).subscribe(
        (blogPost: BlogPost) => {
          this.blog = blogPost;
          this.titleService.setTitle(this.blog.title.concat(' - ' + this.title));
          this.setMetaTags();
          
          // Store in TransferState on server
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(this.BLOG_POST_KEY, blogPost);
          }
        },
        () => this.router.navigateByUrl('blog'),
        () => this.loading = false
      );
    }
  }

  private setMetaTags(): void {
    if (this.blog) {
      const description = this.blog.body?.substring(0, 160) || 'Read this blog post on eFarm.cm';
      const imageUrl = this.hasImagePath(this.blog)
        ? this.getImageSrc(this.blog.imagePath)
        : '/assets/images/blog-img1_1.jpg';
      
      // Standard meta tags
      this.titleService.setTitle(this.blog.title.concat(' - ' + this.title));
      
      // Open Graph tags
      this.meta.updateTag({ property: 'og:title', content: this.blog.title });
      this.meta.updateTag({ property: 'og:description', content: description });
      this.meta.updateTag({ property: 'og:image', content: imageUrl });
      this.meta.updateTag({ property: 'og:type', content: 'article' });
      this.meta.updateTag({ property: 'og:site_name', content: 'eFarm Marketplace' });
      
      // Twitter Card tags
      this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
      this.meta.updateTag({ name: 'twitter:title', content: this.blog.title });
      this.meta.updateTag({ name: 'twitter:description', content: description });
      this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
      
      // Standard description
      this.meta.updateTag({ name: 'description', content: description });
    }
  }

  loadComments() {
        this.blogService.loadComments(parseInt(this.blogPostId, 10)).subscribe(
      (res: IPagination) => this.setConfig(res),
      null,
      () => this.loadingComments = false
    );
  }

  hasImagePath(blog) {
    return blog.imagePath !== '' && blog.imagePath !=  null && blog.imagePath.includes('media');
  }

  getImageSrc(imagePath) {
    return imagePath !== '' && imagePath !== null ?
    environment.image_cdn_url + '/' + imagePath + '?func=bound&width=342&height=256' : '/assets/images/blog-img1_1.jpg';
  }

  saveComment() {
    this.submitting = true;
    this.blogService.saveComment(parseInt(this.blogPostId, 10), this.form.value).subscribe(
      (res: any) => {},
      null,
      () => {
        this.submitting = false;
        this.form.removeControl('body');
        this.form.addControl('body',
        new UntypedFormControl('', [Validators.required]));
        this.loadComments();
      }
    );
  }

  convertPostedDateToISODate(strPostedDate: string) {
    return new Date(strPostedDate).toISOString();
  }

  private setConfig(result: any) {
    this.comments = result.data;
    this.config = {
        currentPage: result.page,
        itemsPerPage: result.itemPerPage,
        totalItems: result.totalOfItems,
        totalOfPage: result.totalOfPage,
        id: 'listing'
    };
  }

  private buildFormComment() {
    this.form = this.fb.group({
      id: new UntypedFormControl(0),
      userId: new UntypedFormControl(this.user != null ? this.user.id : null, [Validators.required]),
      name: new UntypedFormControl(this.user != null ? this.user.firstName + ' ' + this.user.lastName : null, [Validators.required]),
      email: new UntypedFormControl(this.user != null ? this.user.email : '', [Validators.required]),
      city: new UntypedFormControl(this.user != null ? this.user.town : ''),
      body: new UntypedFormControl('', [Validators.required]),
    });
  }

}
